// ── BrandModal — das geteilte Overlay-Primitive der Red.-App ────────────
// Vereinheitlichte „red."-Overlay-Sprache (wie die Navbar): neutrale Glass-
// Fläche, EIN Header-System — Kicker (eyebrow, caps) · Display-Headline ·
// roter 46px-Akzent-Strich (#B33E3A) · neutraler Schließen-Kreis · Hairline.
// EIN Akzent (Mattrot), kein Glow, keine getönten Flächen, keine CropMarks/
// Wortmarke mehr (bewusst zugunsten der ruhigen Navbar-Linie entfernt).
// Motion: zentrierte Karte, ease-out, prefers-reduced-motion respektiert.
//
// Wiederverwendet von ExportSheet (Berichte/Asset) und SupportModal.

import { useEffect } from 'react';
import React from 'react';
import { F, DUR } from '../../styles/theme';
import { useTheme } from '../../context/ThemeContext';
import { Icon, Icons } from './Icons';

// Vereinheitlichte Overlay-Sprache (wie Navbar): neutrale Fläche, EIN Akzent (Mattrot).

function CloseBtn({ T, onClick }) {
  // Subtile, organische Motion: sanfter BG-Fill statt Border-Snap + weiches
  // Press-Squish (leichter Überschwung). Ruhiger als ein harter Farbwechsel.
  return (
    <button
      onClick={onClick}
      aria-label="Schließen"
      onMouseEnter={e => { e.currentTarget.style.background = T.itemHover; e.currentTarget.style.color = T.text; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = T.sub; e.currentTarget.style.transform = 'scale(1)'; }}
      onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.88)'; }}
      onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
      style={{
        width: 32, height: 32, flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        background: 'transparent', color: T.sub,
        border: `1px solid ${T.line}`, borderRadius: 999,
        cursor: 'pointer', padding: 0,
        transition: `background ${DUR.base}ms ease, color ${DUR.base}ms ease, transform ${DUR.base}ms cubic-bezier(.34,1.5,.5,1)`,
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <Icon d={Icons.close} size={13} stroke="currentColor" sw={2.4} />
    </button>
  );
}

export default function BrandModal({
  open,
  onClose,
  eyebrow,          // kurzes Caps-Label rechts neben der Wordmark, z.B. "Export"
  title,            // Display-Headline (mit 2px-Unterkante)
  subtitle,
  children,
  footer,
  maxWidth = 480,
}) {
  const { T } = useTheme();

  // Escape schließt; Body-Scroll sperren solange offen.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(8,8,11,0.66)',
        backdropFilter: 'blur(10px) saturate(120%)',
        WebkitBackdropFilter: 'blur(10px) saturate(120%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
        animation: `brandModalFade ${DUR.fast}ms ease both`,
      }}
    >
      <style>{`
        @keyframes brandModalFade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes brandModalIn {
          from { transform: translateY(6px) scale(0.965); opacity: 0 }
          to   { transform: translateY(0)   scale(1);     opacity: 1 }
        }
        .brandModalCard {
          animation: brandModalIn 260ms cubic-bezier(0.23, 1, 0.32, 1) both;
        }
        @media (prefers-reduced-motion: reduce) {
          .brandModalCard { animation: brandModalFade ${DUR.fast}ms ease both; }
        }
      `}</style>

      <div
        className="brandModalCard"
        onClick={e => e.stopPropagation()}
        style={{
          position: 'relative',
          width: '100%', maxWidth,
          background: T.surface,
          border: `1px solid ${T.line || T.border}`,
          borderRadius: 18, // organisch rund (wie Navbar), nicht mehr sharp 3
          boxShadow: T.cardShadowLg,
          padding: '22px 24px 22px',
          fontFamily: F.family,
        }}
      >
        {/* Kopf (vereinheitlicht): Kicker · Display-Headline · roter Akzent-Strich · neutraler Close */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14 }}>
          <div style={{ minWidth: 0 }}>
            {eyebrow && (
              <div style={{
                fontFamily: F.family, fontSize: 12, fontWeight: 600,
                letterSpacing: '-0.005em', color: T.sub, marginBottom: 4,
              }}>{eyebrow}</div>
            )}
            {title && (
              <h2 style={{
                margin: 0,
                fontFamily: F.display, fontSize: 24, fontWeight: 800,
                letterSpacing: '-0.03em', lineHeight: 1.1, color: T.text,
              }}>{title}</h2>
            )}
          </div>
          <CloseBtn T={T} onClick={onClose} />
        </div>

        {subtitle && (
          <div style={{
            fontSize: 12.5, color: T.sub, lineHeight: 1.55,
            marginTop: 12, maxWidth: 380,
          }}>
            {subtitle}
          </div>
        )}

        <div aria-hidden style={{ height: 1, background: T.line, margin: '18px 0' }} />

        <div>
          {children}
        </div>

        {footer && (
          <div style={{
            marginTop: 14, fontSize: 11.5, color: T.mute || T.sub,
            lineHeight: 1.5, textAlign: 'center',
          }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// ── ChoiceGrid — zwei gleichwertige Kacheln mit zentrierter Haarlinie ──
export function ChoiceGrid({ children }) {
  const { T, isDark } = useTheme();
  const hairline = isDark ? 'rgba(255,255,255,0.10)' : 'rgba(12,12,15,0.10)';
  return (
    <div style={{
      position: 'relative',
      display: 'grid', gridTemplateColumns: '1fr 1fr',
      border: `1px solid ${T.line || T.border}`,
      borderRadius: 16, overflow: 'hidden',
      background: T.bg,
    }}>
      <div aria-hidden style={{
        position: 'absolute', top: 12, bottom: 12, left: '50%',
        width: 1, background: hairline, transform: 'translateX(-0.5px)',
      }} />
      {children}
    </div>
  );
}

// ── ChoiceTile — eine Auswahl-Kachel mit Marken-Motion ──
// Hover: dezenter BG + sanftes Icon-Wachsen. Press: scale(0.97).
// Akzent rein über Stroke — keine getönte Fläche.
export function ChoiceTile({ glyph, label, sub, badge, onClick }) {
  const { T } = useTheme();
  return (
    <button
      onClick={onClick}
      onMouseEnter={e => {
        const el = e.currentTarget;
        el.style.background = T.itemHover || 'rgba(255,255,255,0.05)';
        const ico = el.querySelector('[data-ico]');
        if (ico) ico.style.transform = 'scale(1.06)';
      }}
      onMouseLeave={e => {
        const el = e.currentTarget;
        el.style.background = 'transparent';
        el.style.transform = 'scale(1)';
        const ico = el.querySelector('[data-ico]');
        if (ico) ico.style.transform = 'scale(1)';
      }}
      onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.97)'; }}
      onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
      style={{
        position: 'relative', overflow: 'hidden',
        background: 'transparent', border: 'none',
        padding: '30px 16px 28px',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 13,
        cursor: 'pointer', color: T.text, fontFamily: F.family,
        WebkitTapHighlightColor: 'transparent',
        transition: `background ${DUR.fast}ms ease, transform ${DUR.press}ms cubic-bezier(0.23,1,0.32,1)`,
      }}
    >
      {/* Phosphor-Komponenten sind forwardRef-Objekte — nicht direkt in <svg> renderable. */}
      {glyph && !React.isValidElement(glyph) && (typeof glyph === 'function' || glyph.$$typeof) ? (
        <span data-ico style={{ display: 'block', transition: `transform ${DUR.base}ms cubic-bezier(.34,1.4,.5,1)`, lineHeight: 0 }}>
          {React.createElement(glyph, { size: 36, color: T.text, weight: 'light' })}
        </span>
      ) : (
        <svg
          width={36} height={36} viewBox="0 0 24 24" fill="none"
          stroke={T.text} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"
          data-ico
          style={{ display: 'block', transition: `transform ${DUR.base}ms cubic-bezier(.34,1.4,.5,1)` }}
        >
          {glyph}
        </svg>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
        <div style={{
          fontFamily: F.display, fontSize: 17, fontWeight: 700,
          letterSpacing: '-0.025em', color: T.text, lineHeight: 1.1,
        }}>
          {label}
        </div>
        {sub && (
          <div style={{ fontSize: 11.5, color: T.sub, fontWeight: 500, textAlign: 'center', lineHeight: 1.3 }}>
            {sub}
          </div>
        )}
        {badge && (
          <div style={{
            marginTop: 3, fontSize: 11, fontWeight: 700, letterSpacing: '-0.005em',
            color: T.red,
          }}>
            {badge}
          </div>
        )}
      </div>
    </button>
  );
}
